import React, { useRef } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import Actions from './Actions';
import BackToThreadsListButton from './BackToThreadsListButton';
import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import {
  navigateToLatestRevision,
  restoreRevision,
  selectRevisionID,
} from 'src/presentation/components/pages/sidekick/threads/actions/revisionActions';
import NewThreadModal, {
  NewThreadModalRef,
} from 'src/presentation/components/pages/sidekick/threads/components/NewThreadModal';
import { saveThread } from 'src/presentation/components/pages/sidekick/threads/actions/threadActions';
import {
  selectCurrentThread,
  selectIsDirty,
  setShowLoadingIndicator,
} from 'src/presentation/components/pages/sidekick/threads/actions/threadsUiSlice';
import { THREAD_CANVAS_WIDTH } from 'src/presentation/components/pages/sidekick/threads/SidekickConstants';
import VersionBar from './VersionBar';
import useHandleActivate from 'src/presentation/components/pages/sidekick/threads/hooks/useHandleActivate';
import { ModalWrapperRef } from 'src/presentation/components/pages/sidekick/threads/components/ModalWrapper';

const ControlBarThread = ({ className, root }: { className: string; root: string }) => {
  const thread = useSelector(selectCurrentThread);
  const newThreadModalRef = useRef<NewThreadModalRef>(null);
  const isDirty = useSelector(selectIsDirty);
  const modalRef = React.useRef<ModalWrapperRef>(null);
  const { toggleEnabledHandler } = useHandleActivate({ modalRef });

  return (
    <>
      <NavigationPanel className={className}>
        <VersionBar />
        <Actions root={root} newThreadModalRef={newThreadModalRef} toggleEnabledHandler={toggleEnabledHandler} />
        <SaveButton disabled={!isDirty} newThreadModalRef={newThreadModalRef} thread={thread} />
      </NavigationPanel>

      <NewThreadModal ref={newThreadModalRef} />
    </>
  );
};

const ControlBarRevision = ({ className }: { className: string }) => {
  const dispatch = useDispatch();

  const handleBackToLatest = () => {
    dispatch(navigateToLatestRevision({ history }));
  };
  const handleRestore = () => {
    dispatch(restoreRevision({ history }));
  };

  return (
    <NavigationPanel className={className}>
      <Button type={ButtonTypes.SECONDARY} onClick={handleBackToLatest}>
        Back to latest
      </Button>
      <Button onClick={handleRestore}>Restore</Button>
    </NavigationPanel>
  );
};

const ControlBar = ({ className, root }: { className: string; root: string }) => {
  const revision = useSelector(selectRevisionID);

  if (revision) {
    return <ControlBarRevision className={className} />;
  }

  return <ControlBarThread className={className} root={root} />;
};

type NavigationPanelProps = {
  backButtonLabel?: string;
  children: React.ReactNode;
  className?: string;
};

export const NavigationPanel = ({ children, className }: NavigationPanelProps) => {
  return (
    <>
      <BackToThreadsListButton />
      <StyledNavigationPanel>
        <ControlBarContainer className={className}>{children}</ControlBarContainer>
      </StyledNavigationPanel>
    </>
  );
};

export const SaveButton = ({ disabled, newThreadModalRef, thread }) => {
  const dispatch = useDispatch();

  const handleSave = () => {
    newThreadModalRef.current?.show({
      confirmButtonLabel: 'Save',
      subscribe: (newName: unknown) => {
        dispatch(setShowLoadingIndicator(true));
        setTimeout(() => {
          dispatch(saveThread({ revisionName: newName as string }));
        });
      },
      title: `Save revisions`,
      value: '',
      thread,
      label: 'Provide a brief description of the Thread revisions.',
      placeholder: 'E.g., Updated greeting',
      useSimpleInput: true,
      requireValidation: false,
    });
  };

  return (
    <Button onClick={handleSave} disabled={disabled}>
      Save
    </Button>
  );
};

const StyledNavigationPanel = styled.div`
  display: flex;
  min-width: ${THREAD_CANVAS_WIDTH}px;
  padding: 0 24px;
  position: sticky;
  top: 16px;
  z-index: 4;
`;

const ControlBarContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin: 0 auto;

  white-space: nowrap;
  z-index: 1;

  button {
    box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.08);
  }
`;

export default ControlBar;
